import React from 'react';
import { Link } from 'react-router-dom';
import { Info, ExternalLink, Calculator, FlaskConical } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

// This component provides info about LEI measures with links to the reference page
export const LEIMeasureInfo = ({ measure, showName = false, iconSize = 'sm' }) => {
  if (!measure) return null;

  const iconClass = iconSize === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button 
          className="inline-flex items-center gap-1 text-slate-400 hover:text-teal-600 transition-colors cursor-help"
          onClick={(e) => e.stopPropagation()}
        >
          {showName && <span className="text-sm text-slate-700">{measure.name}</span>}
          <Info className={iconClass} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" side="right" align="start">
        <div className="p-3 border-b bg-slate-50">
          <h4 className="font-semibold text-slate-900 text-sm">{measure.name}</h4>
          <p className="text-xs text-slate-500">{measure.pillar}</p>
        </div>
        <div className="p-3 space-y-3 max-h-[500px] overflow-y-auto">
          <p className="text-sm text-slate-700">{measure.description}</p>
          
          {measure.what_it_measures && (
            <div>
              <h5 className="text-xs font-semibold text-slate-600 mb-1">What It Measures</h5>
              <p className="text-xs text-slate-600">{measure.what_it_measures}</p>
            </div>
          )}
          
          {measure.typical_range && (
            <div className="p-2 bg-amber-50 rounded text-xs">
              <span className="font-medium text-amber-700">Typical Range: </span>
              <span className="text-amber-800">{measure.typical_range}</span>
            </div>
          )}
          
          {/* LEI Formula Section - How to calculate this measure */}
          {measure.lei_formula && (
            <div className="p-2 bg-blue-50 rounded border border-blue-200">
              <div className="flex items-center gap-1 mb-2">
                <FlaskConical className="h-3 w-3 text-blue-600" />
                <h5 className="text-xs font-semibold text-blue-700">How to Calculate This LEI</h5>
              </div>
              <p className="text-xs font-mono bg-white p-1.5 rounded border text-blue-800 mb-2">
                {measure.lei_formula.formula}
              </p>
              <div className="space-y-1 mb-2">
                {measure.lei_formula.variables?.slice(0, 2).map((variable, idx) => (
                  <div key={variable.name || `var-${idx}`} className="text-[10px]">
                    <span className="font-medium text-blue-700">{variable.name}: </span>
                    <span className="text-slate-600">{variable.description}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-blue-600 italic">
                {measure.lei_formula.example?.substring(0, 100)}...
              </p>
            </div>
          )}
          
          {/* Calculation Logic Section - How this LEI impacts KPIs */}
          {measure.calculation_logic && (
            <div className="p-2 bg-slate-100 rounded border border-slate-200">
              <div className="flex items-center gap-1 mb-2">
                <Calculator className="h-3 w-3 text-teal-600" />
                <h5 className="text-xs font-semibold text-slate-700">How This LEI Impacts KPIs</h5>
              </div>
              <p className="text-xs font-mono bg-white p-1.5 rounded border text-slate-700 mb-2">
                {measure.calculation_logic.formula}
              </p>
              <p className="text-[10px] text-slate-600 mb-2">
                <span className="font-medium">Example: </span>
                {measure.calculation_logic.example}
              </p>
              <div className="space-y-1">
                {measure.calculation_logic.components?.slice(0, 3).map((comp, idx) => (
                  <div key={comp.name || `comp-${idx}`} className="text-[10px] flex gap-1">
                    <span className="font-medium text-slate-600 min-w-[80px]">{comp.name}:</span>
                    <span className="text-slate-500">{comp.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <Link
            to={`/lei-reference#${measure.id}`}
            className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            View full details in LEI Measures Guide
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Simple tooltip version for compact spaces
export const LEIMeasureTooltip = ({ measure, children }) => {
  if (!measure) return children;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium text-sm">{measure.name}</p>
            <p className="text-xs text-slate-400">{measure.pillar}</p>
            <p className="text-xs">{measure.description}</p>
            {measure.typical_range && (
              <p className="text-xs text-amber-600">Range: {measure.typical_range}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default LEIMeasureInfo;
